<template>
    <ApiViewWrapper :notFound="notFound" :allowAnonymous="false">
        <DefaultLayout>
            <div class="collapsable-header p-2 border">
                <UserDetailHeader :user="user" @goBack="goBack" />
            </div>
            <SectionManager sectionType="none" stickyY="auto">
                <SectionItem>
                    <template #title>
                        {{ i18n('Main Information') }}
                    </template>
                    <div class="bg-white p-2 border">
                        <div class="row g-2 align-items-center">
                            <div class="col">
                                <h2 class="h5">
                                    {{ i18n('Main Information') }}
                                </h2>
                            </div>
                            <div class="col text-end">
                                <ActionDropdown ref="actions" :actions="actions" />
                            </div>
                        </div>
                        <div class="row g-2 mt-0">
                            <div class="col-6 col-sm-6">
                                <div class="row g-2 mt-0">
                                    <div class="col-12">
                                        <FieldReadonly :value="user.userType" :label="i18n('User Type')"
                                            format="object" />
                                    </div>

                                    <UserFeatureFieldReadonly class="col-12" field_name="email" :user="user"
                                        :label="i18n('Email')" />

                                    <UserFeatureFieldReadonly class="col-12" field_name="alternative_email" :user="user"
                                        :label="i18n('Alternative email')" />

                                    <div class="col-12">
                                        <template v-if="user?.metadata?.AD_STATUS === 'PENDING'">
                                            <div class="read-only">
                                                <div class="small text-muted">
                                                    {{ i18n('Short user') }}
                                                </div>
                                                <Tooltip :title="i18n('Pending update on AD')">
                                                    <span class="text-danger me-1">⚠</span>
                                                </Tooltip>
                                                <FormattedValue :value="getUserOriginIdentifier()" />
                                            </div>
                                        </template>
                                        <template v-else>
                                            <UserFeatureFieldReadonly class="col-12" field_name="origin_identifier"
                                                :user="user" :label="i18n('Origin Identifier')" />
                                            <UserFeatureFieldReadonly class="col-12" field_name="short_name_adsolvia"
                                                :user="user" :label="i18n('Short name adsolvia')" />
                                        </template>
                                    </div>

                                    <UserFeatureFieldReadonly class="col-12" field_name="long_name_adsolvia"
                                        :user="user" :label="i18n('Long name adsolvia')" />

                                    <UserFeatureFieldReadonly class="col-12" field_name="short_name_intrum" :user="user"
                                        :label="i18n('Short name intrum')" />

                                    <UserFeatureFieldReadonly class="col-12" field_name="long_name_intrum" :user="user"
                                        :label="i18n('Long name intrum')" />

                                    <div class="col-12" v-if="user?.metadata?.AD_STATUS">
                                        <FieldReadonly :value="i18n(user.metadata.AD_STATUS)"
                                            :label="i18n('AD Status')" />
                                    </div>

                                </div>
                            </div>
                            <div class="col-6">
                                <div class="row g-2 mt-0">
                                    <!-- Responsible -->
                                    <div class="col-12" v-if="$root.features?.users?.responsible?.enabled">
                                        <FieldReadonly
                                            :value="user.responsibleUser !== null ? user.responsibleUser?.first_name + ' ' + user.responsibleUser?.last_name : '--'"
                                            :label="i18n('Responsible User')" format="object" />
                                    </div>

                                    <!-- level -->
                                    <div class="col-12 col-sm-6"
                                        v-if="user.userType?.code === 'employee' && user.company">
                                        <FieldReadonly :value="user.userLevel" :label="i18n('Level')" format="object" />
                                    </div>

                                    <div class="col-12"
                                        v-if="user.userType?.code === 'employee' && user.company && ($root.features?.position || true)">
                                        <FieldReadonly :value="user.employee_number" :label="i18n('Employee Number')" />
                                    </div>

                                    <!-- Departament -->
                                    <div class="col-12" v-if="user.company">
                                        <FieldReadonly :value="user.company" :label="i18n('Company')" format="object" />
                                    </div>
                                    <div class="col-12"
                                        v-if="user.userType?.code === 'employee' && user.company && $root.features?.offices?.enabled">
                                        <FieldReadonly :value="user.office" :label="i18n('Office')" format="object" />
                                    </div>
                                    <div class="col-12"
                                        v-if="user.userType?.code === 'employee' && user.company && $root.features?.department?.enabled">
                                        <FieldReadonly :value="user.department" :label="i18n('Department')"
                                            format="object" />
                                    </div>

                                    <!-- Rol -->
                                    <div class="col-12"
                                        v-if="user.userType?.code === 'employee' && user.company && $root.features?.department?.enabled">
                                        <FieldReadonly :value="user.role" :label="i18n('Role')" format="object" />
                                    </div>

                                    <div class="col-12"
                                        v-if="user.acl?.viewDocument && user.documentType?.name && user.document">
                                        <FieldReadonly :value="user.documentType?.name + ' / ' + user.document"
                                            :label="i18n('Document')" />
                                    </div>

                                    <div class="col-12"
                                        v-if="user.userType?.code === 'employee' && $root.features?.users?.authorizedBy?.enabled">
                                        <FieldReadonly :value="user.authorizedByExternalUser"
                                            :label="i18n('Authorized By')" format="object" />
                                    </div>

                                    <div class="col-12"
                                        v-if="user.userType?.code === 'employee' && $root.features?.users?.authorizedBy?.enabled">
                                        <FieldReadonly :value="user.authorization_date"
                                            :label="i18n('Authorization Date')" format="date" />
                                    </div>

                                    <div class="col-12" v-if="$root.features?.users?.telephone?.enabled">
                                        <FieldReadonly
                                            :value="(user.telephone != null ? user.telephone : '') + (user.telephone_extension != null ? ' / ' + user.telephone_extension : '') + (user.contact_telephone != null ? ' / ' + user.contact_telephone : '')"
                                            :label="i18n('Mobile / Extension / Contact Tel.')" />
                                    </div>

                                    <div class="col-12" v-if="user?.metadata?.AD_PASSWORD_EXPIRATION">
                                        <FieldReadonly :value="user?.metadata?.AD_PASSWORD_EXPIRATION"
                                            :label="i18n('Password Expiration Date')" format="date" />
                                    </div>

                                    <div class="col-12">
                                        <FieldReadonly :value="user.ending_date" :label="i18n('End Date')"
                                            format="date" />
                                    </div>

                                    <div class="col-12">
                                        <FieldReadonly :value="user.comments" :label="i18n('Comments')" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </SectionItem>
                <SectionItem v-if="user.acl?.viewUserProfile">
                    <template #title>
                        {{ i18n('Profiles') }}
                    </template>
                    <UserUserProfileSection :user="user" @updated="reload" :groupByApp="true" />
                </SectionItem>
            </SectionManager>
            <UserModal ref="UserModal" @updated="reload" />
            <UserResetPasswordModal ref="UserResetPasswordModal" @updated="reload" />
        </DefaultLayout>
    </ApiViewWrapper>
</template>

<script>
import api from "@/api.js";
import { canAccessPage } from "@/routeAcl.js";
import { hasSingleUserOrigin } from "@/components/user/userOrigin.js";
import ActionDropdown from "@/components/common/list/ActionDropdown.vue";
import ApiViewWrapper from "@/components/common/view/ApiViewWrapper.vue";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import FieldReadonly from "@/components/common/form/FieldReadonly.vue";
import FormattedValue from "@/components/common/form/FormattedValue.vue";
import SectionItem from "@/components/common/layout/section/SectionItem.vue";
import SectionManager from "@/components/common/layout/section/SectionManager.vue";
import Tooltip from "@/components/common/layout/Tooltip.vue";
import UserDetailHeader from "@/components/user/UserDetailHeader.vue";
import UserModal from "@/components/user/UserModal.vue";
import UserUserProfileSection from "@/components/user/UserUserProfileSection.vue";
import UserResetPasswordModal from "@/components/user/UserResetPasswordModal.vue";
import UserFeatureFieldReadonly from "@/components/user/UserFeatureFieldReadonly.vue";

export default {
    components: {
        ActionDropdown,
        ApiViewWrapper,
        DefaultLayout,
        FieldReadonly,
        FormattedValue,
        SectionItem,
        SectionManager,
        Tooltip,
        UserDetailHeader,
        UserModal,
        UserUserProfileSection,
        UserResetPasswordModal,
        UserFeatureFieldReadonly,
    },
    data() {
        return {
            user: null,
            notFound: null,
        };
    },
    mounted() {
        api.auth().then((user) => {
            this.$root.loggedUser = user;

            if (!user.loggedIn) {
                return; // Layout non-anonymous will handler redirect
            }

            if (!canAccessPage(this.$route?.name, user, this.$root.features)) {
                this.$router.push({ name: 'Index', });
                return;
            }

            this.load();
        });

    },
    watch: {
        "$route.params.id"() {
            this.load();
        },
    },
    computed: {
        actions() {
            return [{
                label: this.i18n('Edit'),
                fn: () => {
                    this.showUserModal();
                },
                if: (this.user.acl?.update) === true,
                starred: true,
            },
            {
                label: this.i18n('Do Discharge'),
                fn: () => {
                    this.$root.requestConfirmation(this.i18n('Do Discharge?'), () => {
                        this.$emit('updating', this.user);
                        api.delete(`users/${this.user.id}`).then(() => {
                            this.reload();
                        });
                    });
                },
                if: (this.user.acl?.delete) === true,
                starred: false,
            }, {
                label: this.i18n('Do Restore'),
                fn: () => {
                    this.$root.requestConfirmation(this.i18n('Do Restore?'), () => {
                        this.$emit('updating', this.user);
                        api.post(`users/${this.user.id}/restore`).then(() => {
                            this.reload();
                        });
                    });
                },
                if: (this.user.acl?.restore) === true,
                starred: true,
            }, {
                label: this.i18n('Reset password'),
                fn: () => {
                    this.showUserResetPasswordModal();
                },
                if: (this.user.acl?.updateAdPassword) === true,
                starred: false,
            }, {
                label: this.i18n('Force 2FA configuration'),
                fn: () => {
                    this.$root.requestConfirmation(this.i18n('Do force 2FA configuration?'), () => {
                        api.put('users/' + this.user.id, { 'is_2fa_enabled': 0, 'google2fa_secret': null }).then(() => {
                            this.reload();
                        });
                    });
                },
                if: (this.user.acl?.updateAdPassword) === true,
                starred: false,
            },
            ];
        },
    },
    hasSingleUserOrigin,
    methods: {
        goBack() {
            this.$router.push({
                name: 'UserIndex',
            });
        },
        load(force) {
            clearTimeout(this.loadTimeout);
            this.notFound = null;

            if (!force) {
                this.loadTimeout = setTimeout(() => {
                    this.load(true);
                }, 250);
                return;
            }

            if (!this.$route.params.id) {
                this.notFound = true;
                return;
            }

            api.get(`users/${this.$route.params.id}`, { acl: 'user' }).then((user) => {
                if (user.acl?.view !== true) {
                    this.$router.push({ name: 'UserIndex', });
                    return;
                }

                this.user = user;
                this.notFound = false;
            }).catch(() => {
                this.notFound = true;
            });
        },
        reload() {
            this.load();
        },
        showUserModal() {
            if (!this.$refs.UserModal) return;
            this.$refs.UserModal.show(this.user.id);
        },
        showUserResetPasswordModal() {
            if (!this.$refs.UserResetPasswordModal) return;
            this.$refs.UserResetPasswordModal.show(this.user.id);
        },
        getUserOriginIdentifier() {
            let identifier = null;

            if (this.$root.features?.users?.features) {
                identifier = this.$root.features?.users?.features['short_name_adsolvia'] ?? this.$root.features?.users?.features['origin_identifier'] ?? null;
            }
            return identifier ?? this.user.origin_identifier;
        },
    },
};
</script>